// Live tank-level store (mocked, editable from /admin/tank-levels)
// Persisted to localStorage so admin edits survive reloads, and shared
// across portal + intranet via a small subscription pattern.

import { useEffect, useState } from "react";
import { CHEM_VESSELS } from "@/data/chemCarriageData";
import { PLAN_TANKS } from "@/data/tankPlanLayout";

const KEY = "voff.tankLevels.v1";

export type LevelMap = Record<string, Record<string, number>>; // vessel → tankId → %

function seedDefaults(): LevelMap {
  const out: LevelMap = {};
  for (const v of CHEM_VESSELS) {
    out[v.name] = {};
    for (const t of PLAN_TANKS) {
      if (!t.fillable) continue;
      // Sensible defaults by group
      let pct = 0;
      if (t.group === "cargo")    pct = Math.round(40 + Math.random() * 50);
      if (t.group === "ballast")  pct = Math.round(20 + Math.random() * 60);
      if (t.group === "fuel")     pct = Math.round(55 + Math.random() * 35);
      if (t.group === "service")  pct = Math.round(50 + Math.random() * 40);
      if (t.group === "slop")     pct = Math.round(5  + Math.random() * 25);
      out[v.name][t.id] = pct;
    }
  }
  return out;
}

function read(): LevelMap {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  const seed = seedDefaults();
  try { localStorage.setItem(KEY, JSON.stringify(seed)); } catch {}
  return seed;
}

const listeners = new Set<() => void>();
let cache: LevelMap | null = null;

function notify() { listeners.forEach(l => l()); }

export function getLevels(): LevelMap {
  if (!cache) cache = read();
  return cache;
}

export function setVesselLevels(vessel: string, levels: Record<string, number>) {
  const all = { ...getLevels(), [vessel]: levels };
  cache = all;
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch {}
  notify();
}

export function setTankLevel(vessel: string, tankId: string, pct: number) {
  const all = getLevels();
  const v = { ...(all[vessel] || {}), [tankId]: Math.max(0, Math.min(100, Math.round(pct))) };
  setVesselLevels(vessel, v);
}

export function resetAllLevels() {
  cache = seedDefaults();
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch {}
  notify();
}

// Cross-tab sync
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) { cache = null; notify(); }
  });
}

export function useTankLevels(vessel: string) {
  const [, setT] = useState(0);
  useEffect(() => {
    const l = () => setT(t => t + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return getLevels()[vessel] || {};
}
